import { TagSpecDto } from '@ampsec/platform-client';

/** Minimal shape for tag filter dropdowns and chip selectors. */
export type TagSpecListOption = {
    id: string;
    name: string;
};

/**
 * Flattens `{ tagSpec }` wrappers so callers can treat list items as `TagSpecDto`.
 *
 * @param specs - Raw catalog rows from the API or cache (flat or wrapped).
 * @returns Flat `TagSpecDto` array in the same order as the input.
 */
export function normalizeTagSpecList(specs: TagSpecDto[]): TagSpecDto[] {
    return specs.map((item) => (item as TagSpecDto & { tagSpec?: TagSpecDto }).tagSpec ?? item);
}

/**
 * Maps catalog rows to `{ id, name }` options for dropdowns and chips.
 *
 * @param specs - Tag specs from `useTagSpecsCatalog` or equivalent.
 * @returns Options with both `id` and `name`; invalid rows are omitted.
 */
export function tagSpecsToOptions(specs: TagSpecDto[]): TagSpecListOption[] {
    return normalizeTagSpecList(specs)
        .map((spec) => ({
            id: spec.id,
            name: spec.name,
        }))
        .filter((spec): spec is TagSpecListOption => Boolean(spec.id && spec.name));
}

/**
 * Sorted, deduplicated tag names for multi-select filters (e.g. findings grid).
 *
 * @param specs - Tag specs from the tenant catalog (tenant + global when merged).
 * @returns Unique tag names, locale-sorted ascending.
 */
export function tagSpecsToUniqueNames(specs: TagSpecDto[]): string[] {
    const names = normalizeTagSpecList(specs)
        .map((spec) => spec.name)
        .filter((name): name is string => Boolean(name));
    return Array.from(new Set(names)).sort();
}

/**
 * Resolves tag spec ids to display names for grids and flow-spec tag filters.
 *
 * @param specs - Tag specs from the catalog hook or cache.
 * @returns Map of tag spec id → display name (last wins if ids duplicate).
 */
export function buildTagSpecIdToNameLookup(specs: TagSpecDto[]): Record<string, string> {
    const lookup: Record<string, string> = {};
    tagSpecsToOptions(specs).forEach((spec) => {
        lookup[spec.id] = spec.name;
    });
    return lookup;
}
